import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HomeIcon, BookOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

// Floating Book Page Component
const BookPage: React.FC<{ delay: number; x: string; y: string; rotation: number }> = ({
  delay,
  x,
  y,
  rotation,
}) => (
  <motion.div
    className="absolute"
    style={{ left: x, top: y }}
    initial={{ opacity: 0, rotate: rotation - 20 }}
    animate={{
      opacity: [0.4, 0.7, 0.4],
      y: [0, -15, 0],
      rotate: [rotation - 5, rotation + 5, rotation - 5],
    }}
    transition={{
      duration: 6,
      repeat: Infinity,
      delay,
      ease: 'easeInOut',
    }}
  >
    <svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Page shape */}
      <path
        d="M2 4C2 2.89543 2.89543 2 4 2H32C33.6569 2 35 3.34315 35 5V45C35 46.6569 33.6569 48 32 48H4C2.89543 48 2 47.1046 2 46V4Z"
        fill="rgba(148, 163, 184, 0.15)"
        stroke="rgba(148, 163, 184, 0.3)"
        strokeWidth="1"
      />
      {/* Text lines */}
      <line x1="8" y1="12" x2="28" y2="12" stroke="rgba(148, 163, 184, 0.2)" strokeWidth="2" />
      <line x1="8" y1="18" x2="24" y2="18" stroke="rgba(148, 163, 184, 0.2)" strokeWidth="2" />
      <line x1="8" y1="24" x2="26" y2="24" stroke="rgba(148, 163, 184, 0.2)" strokeWidth="2" />
      <line x1="8" y1="30" x2="20" y2="30" stroke="rgba(148, 163, 184, 0.2)" strokeWidth="2" />
    </svg>
  </motion.div>
);

// Knowledge Graph Node Component
const KnowledgeNode: React.FC<{ x: string; y: string; size: number; delay: number }> = ({
  x,
  y,
  size,
  delay,
}) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      left: x,
      top: y,
      width: size,
      height: size,
      background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)',
      boxShadow: '0 0 15px rgba(6, 182, 212, 0.2)',
    }}
    animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.6, 0.3],
    }}
    transition={{
      duration: 4,
      repeat: Infinity,
      delay,
      ease: 'easeInOut',
    }}
  />
);

// Connection Line Component
const ConnectionLine: React.FC<{ x1: string; y1: string; x2: string; y2: string; delay: number }> = ({
  x1,
  y1,
  x2,
  y2,
  delay,
}) => (
  <motion.svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    style={{ zIndex: 0 }}
    initial={{ opacity: 0 }}
    animate={{ opacity: [0.1, 0.3, 0.1] }}
    transition={{ duration: 5, repeat: Infinity, delay, ease: 'easeInOut' }}
  >
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke="url(#lineGradient)"
      strokeWidth="1"
      strokeDasharray="4 4"
    />
    <defs>
      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.3" />
      </linearGradient>
    </defs>
  </motion.svg>
);

const NotFound: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center px-6 relative overflow-hidden">
      {/* Knowledge Graph Background */}
      <div className="absolute inset-0">
        <KnowledgeNode x="10%" y="20%" size={12} delay={0} />
        <KnowledgeNode x="85%" y="15%" size={8} delay={0.5} />
        <KnowledgeNode x="75%" y="70%" size={10} delay={1} />
        <KnowledgeNode x="15%" y="75%" size={14} delay={1.5} />
        <KnowledgeNode x="50%" y="10%" size={6} delay={2} />
        <KnowledgeNode x="90%" y="45%" size={8} delay={2.5} />
        <ConnectionLine x1="10%" y1="20%" x2="50%" y2="10%" delay={0} />
        <ConnectionLine x1="85%" y1="15%" x2="90%" y2="45%" delay={0.5} />
        <ConnectionLine x1="75%" y1="70%" x2="90%" y2="45%" delay={1} />
      </div>

      {/* Floating Book Pages */}
      <BookPage delay={0} x="8%" y="25%" rotation={-15} />
      <BookPage delay={0.8} x="88%" y="20%" rotation={20} />
      <BookPage delay={1.6} x="5%" y="65%" rotation={10} />
      <BookPage delay={2.4} x="85%" y="60%" rotation={-25} />
      <BookPage delay={3.2} x="15%" y="45%" rotation={5} />
      <BookPage delay={4} x="80%" y="40%" rotation={-10} />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/30 to-[#030712]" />

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 max-w-lg w-full"
      >
        <div
          className="text-center p-8 md:p-12 rounded-3xl border border-slate-700/50"
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          {/* Magnifying Glass Icon */}
          <motion.div
            animate={{
              rotate: [0, 10, -10, 0],
              y: [0, -5, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="flex justify-center mb-4"
          >
            <div
              className="p-3 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
                border: '1px solid rgba(6, 182, 212, 0.2)',
              }}
            >
              <MagnifyingGlassIcon className="w-8 h-8 text-cyan-400" />
            </div>
          </motion.div>

          {/* 404 Number */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.2,
            }}
          >
            <span
              className="text-7xl md:text-8xl font-black bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 50%, #8B5CF6 100%)',
              }}
            >
              404
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-4 text-2xl md:text-3xl font-bold text-white"
          >
            This chapter doesn't exist
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-3 text-slate-400 text-base md:text-lg"
          >
            Looks like this page got lost between the covers. Let's find your way back.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all"
              style={{
                background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 50px rgba(6, 182, 212, 0.5)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <HomeIcon className="w-5 h-5" />
              Back to Home
            </Link>
            <Link
              to="/library"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-slate-300 font-semibold bg-slate-800/50 border border-slate-700 hover:border-cyan-500/50 hover:text-white transition-all"
            >
              <BookOpenIcon className="w-5 h-5" />
              Browse Library
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default NotFound;
